import { useRef, useState } from 'react';

import { cn } from '@/lib/utils';
import { PRESET_AMOUNTS } from '@/constants';
import {
  parseAmountInput,
  snapAmountDown,
  snapAmountUp,
  validateAmount,
} from '@/utils/helpers/amount.helpers';
import InfoSheet from '@/components/InfoSheet';
import { track } from '@/analytics';

type ContributeCounterProps = {
  amount: number;
  minimumAmount: number;
  maximumAmount: number;
  stepValue: number;
  onChange: (value: number) => void;
};

const stepButtonClasses =
  'flex size-[54px] lg:size-14 shrink-0 cursor-pointer items-center justify-center rounded-full border-none bg-[#e7e3da] text-[#3a423d] transition-opacity disabled:cursor-not-allowed disabled:opacity-40';

const chipBaseClasses =
  'h-11 flex-1 cursor-pointer rounded-[13px] border-[1.5px] text-sm font-bold transition-all';
const chipActiveClasses = 'border-[#256956] bg-[#256956] text-white';
const chipInactiveClasses = 'border-[#e3e0d8] bg-white text-[#3a423d]';

function ContributeCounter({
  amount,
  minimumAmount,
  maximumAmount,
  stepValue,
  onChange,
}: ContributeCounterProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [allowanceOpen, setAllowanceOpen] = useState(false);

  const validation = validateAmount(amount, minimumAmount, maximumAmount);
  const isCustom = !PRESET_AMOUNTS.includes(amount);
  const maxLabel = `£${maximumAmount.toLocaleString('en-GB')}`;

  const focusInput = () => {
    inputRef.current?.focus();
    inputRef.current?.select();
  };

  return (
    <div className='flex flex-col'>
      <div className='flex items-center gap-3'>
        <button
          type='button'
          onClick={() => onChange(snapAmountDown(amount, stepValue, minimumAmount))}
          disabled={amount <= minimumAmount}
          aria-label={`Remove £${stepValue}`}
          data-testid='decrement-button'
          className={stepButtonClasses}
        >
          <svg
            width='20'
            height='20'
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth='2.4'
            strokeLinecap='round'
            aria-hidden='true'
          >
            <path d='M5 12h14' />
          </svg>
        </button>
        <div
          data-testid='amount-display'
          className={cn(
            'flex h-15.5 lg:h-16 flex-1 items-center justify-center gap-1.5 rounded-[15px] border-[1.5px] px-4.5 transition-colors',
            validation.valid
              ? 'border-[#e3e0d8] bg-white'
              : 'border-[#f43f5e] bg-[#fff1f2]',
          )}
        >
          <span className='text-2xl font-semibold text-[#9a9893]' aria-hidden='true'>
            £
          </span>
          <input
            ref={inputRef}
            inputMode='numeric'
            autoComplete='off'
            maxLength={6}
            value={amount === 0 ? '' : String(amount)}
            onChange={(e) => onChange(parseAmountInput(e.target.value))}
            aria-label='Gift amount in pounds'
            data-testid='amount-input'
            className='w-[2.8em] border-none bg-transparent p-0 text-center text-[28px] lg:text-[30px] font-bold text-[#1a201d] outline-none'
          />
        </div>
        <button
          type='button'
          onClick={() =>
            onChange(snapAmountUp(amount, stepValue, minimumAmount, maximumAmount))
          }
          aria-label={`Add £${stepValue}`}
          data-testid='increment-button'
          className={stepButtonClasses}
        >
          <svg
            width='20'
            height='20'
            viewBox='0 0 24 24'
            fill='none'
            stroke='currentColor'
            strokeWidth='2.4'
            strokeLinecap='round'
            aria-hidden='true'
          >
            <path d='M12 5v14M5 12h14' />
          </svg>
        </button>
      </div>

      <div className='mt-3 flex gap-2'>
        {PRESET_AMOUNTS.map((preset) => (
          <button
            key={preset}
            type='button'
            onClick={() => onChange(preset)}
            data-testid={`preset-chip-${preset}`}
            className={cn(
              chipBaseClasses,
              amount === preset ? chipActiveClasses : chipInactiveClasses,
            )}
          >
            £{preset}
          </button>
        ))}
        <button
          type='button'
          onClick={focusInput}
          data-testid='preset-chip-custom'
          className={cn(
            chipBaseClasses,
            isCustom ? chipActiveClasses : chipInactiveClasses,
          )}
        >
          Custom
        </button>
      </div>

      {validation.valid ? (
        <p className='mx-0.5 mt-2.5 text-left text-[12.5px] text-[#8a938c]'>
          Please enter an amount between £{minimumAmount} and {maxLabel}.
          Double-check it before continuing, it can't be changed afterwards
          here.
        </p>
      ) : (
        <>
          <button
            type='button'
            onClick={() => {
              track('infoSheetOpened', { sheet: 'allowance' });
              setAllowanceOpen(true);
            }}
            className='mx-0.5 mt-2.5 flex cursor-pointer items-center gap-1.75 self-start border-none bg-transparent p-0 text-[13px] font-bold text-[#9f1239]'
          >
            <svg
              width='15'
              height='15'
              viewBox='0 0 24 24'
              fill='none'
              stroke='currentColor'
              strokeWidth='2.1'
              strokeLinecap='round'
              strokeLinejoin='round'
              aria-hidden='true'
            >
              <circle cx='12' cy='12' r='9.2' />
              <path d='M12 11v5.2M12 7.6h.01' />
            </svg>
            <span className='border-b border-[#9f1239]/40'>
              How much can I gift?
            </span>
          </button>
          <div
            role='alert'
            data-testid='amount-error'
            className='mx-0.5 mt-2.5 flex items-start gap-2.5 rounded-[13px] border border-[#fecdd3] bg-[#fff1f2] text-left px-3.5 py-3'
          >
            <svg
              width='18'
              height='18'
              viewBox='0 0 24 24'
              fill='none'
              stroke='#9f1239'
              strokeWidth='2.2'
              strokeLinecap='round'
              aria-hidden='true'
              className='mt-px shrink-0'
            >
              <circle cx='12' cy='12' r='9' />
              <path d='M12 8v4.5M12 16h.01' />
            </svg>
            <div className='min-w-0 flex-1'>
              <div className='text-[13.5px] font-bold text-[#9f1239]'>
                {validation.errorTitle}
              </div>
              {validation.errorBody && (
                <div className='mt-0.5 text-[12.5px] leading-snug text-[#9f1239]'>
                  {validation.errorBody}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Shares the information sheet the trust tiles use, so the four sheets
          look and behave alike (AC 6, AC 9, AC 10). */}
      <InfoSheet
        sheet={allowanceOpen ? 'allowance' : null}
        onOpenChange={setAllowanceOpen}
      />
    </div>
  );
}

export default ContributeCounter;
